import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Chip,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    LocationOn as LocationIcon,
    Domain as DomainIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import type { TenantWithStatus } from '../types';
import { TenantStatusIndicator } from './TenantStatusIndicator';

export interface TenantDetailsProps {
    open: boolean;
    tenant: TenantWithStatus | null;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onEdit?: (tenant: TenantWithStatus) => void;
}

export function TenantDetails({ open, tenant, loading = false, error = null, onClose, onEdit }: TenantDetailsProps) {
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Reset loading state when dialog opens/closes
    useEffect(() => {
        if (!open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setDetailsLoading(false);
        }
    }, [open]);

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatRelativeTime = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInMs = now.getTime() - date.getTime();
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

            if (diffInDays === 0) {
                return 'Today';
            } else if (diffInDays === 1) {
                return 'Yesterday';
            } else if (diffInDays < 7) {
                return `${diffInDays} days ago`;
            } else if (diffInDays < 30) {
                const weeks = Math.floor(diffInDays / 7);
                return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
            } else {
                const months = Math.floor(diffInDays / 30);
                return `${months} month${months > 1 ? 's' : ''} ago`;
            }
        } catch {
            return 'Unknown';
        }
    };

    const handleEdit = () => {
        if (tenant && onEdit) {
            onEdit(tenant);
        }
    };

    const renderLoadingState = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading tenant details...
            </Typography>
        </Box>
    );

    const renderErrorState = () => (
        <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">{error || 'Failed to load tenant details'}</Typography>
        </Alert>
    );

    const renderTenantInfo = () => {
        if (!tenant) return null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Header Section */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h5" component="h2" gutterBottom>
                                {tenant.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                ID: {tenant.tenantId}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TenantStatusIndicator tenant={tenant} />
                            {onEdit && (
                                <Tooltip title="Edit Tenant">
                                    <IconButton onClick={handleEdit} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Divider />

                {/* Basic Information */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon color="primary" />
                            Basic Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Tenant Name
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {tenant.name}
                                </Typography>
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DomainIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Subdomain
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                                            {tenant.subDomain}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon color="action" fontSize="small" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        AWS Region
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {tenant.region}
                                    </Typography>
                                </Box>
                            </Box>
                            {tenant.userCount !== undefined && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Active Users
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {tenant.userCount.toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Audit Information */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon color="primary" />
                            Audit Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                            <Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Created
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {formatDate(tenant.createdAt)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatRelativeTime(tenant.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Last Modified
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {tenant.updatedAt ? formatDate(tenant.updatedAt) : 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {tenant.updatedAt ? formatRelativeTime(tenant.updatedAt) : ''}
                                </Typography>
                            </Box>
                            {tenant.lastActivity && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Last Activity
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {formatDate(tenant.lastActivity)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatRelativeTime(tenant.lastActivity)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Initial User Information */}
                {tenant.initialUser && (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="primary" />
                                Initial Administrator
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {tenant.initialUser.firstName} {tenant.initialUser.lastName}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Email
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {tenant.initialUser.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Configuration Information */}
                {tenant.configuration && (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Configuration
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {Object.entries(tenant.configuration).map(([key, value]) => (
                                    <Chip key={key} label={`${key}: ${value}`} variant="outlined" size="small" />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Error Information */}
                {tenant.errorMessage && (
                    <Alert severity="error">
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                            Error Details
                        </Typography>
                        <Typography variant="body2">{tenant.errorMessage}</Typography>
                    </Alert>
                )}

                {/* Health Score */}
                {tenant.healthScore !== undefined && (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Health Score
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                    variant="h4"
                                    color={tenant.healthScore >= 80 ? 'success.main' : tenant.healthScore >= 60 ? 'warning.main' : 'error.main'}
                                >
                                    {tenant.healthScore}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {tenant.healthScore >= 80 ? 'Excellent' : tenant.healthScore >= 60 ? 'Good' : 'Needs Attention'}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: { minHeight: '600px', maxHeight: '90vh' },
                },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5" component="h2">
                        Tenant Details
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {loading || detailsLoading ? renderLoadingState() : error ? renderErrorState() : renderTenantInfo()}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
                {tenant && onEdit && (
                    <Button onClick={handleEdit} variant="contained" startIcon={<EditIcon />}>
                        Edit Tenant
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
